import {z} from 'zod';
import {t} from '../../../common/getTranslations';
import {requestContext} from '../../../common/requestContext';
import {IS_CLIENT_SIDE} from '../../../common/shared/utils/constants';
import {buildMetadata} from '../../../common/zodForm/helper';
import {LocaleType, TranslateKey} from '../../../locale/type';
import {ALLOWED_FILE_EXTENSIONS, AskSubCategory, AskType} from '../constant';
import {AskCreateRequestParams} from '../type';

export type FormFieldKeys = keyof Pick<
  AskCreateRequestParams,
  | 'sendEmail'
  | 'telNum'
  | 'content'
  | 'files'
  | 'paymentType'
  | 'bankAccount'
  | 'bankName'
  | 'bankAccountNm'
  | 'orderNo'
  | 'appVersion'
  | 'osVersion'
  | 'deviceModel'
  | 'ggOrderNo'
  | 'osOrderNo'
>;

const FormFieldSchema: Record<
  LocaleType,
  Record<AskType, Record<AskSubCategory, z.ZodObject<z.ZodRawShape>>>
> = {} as any;

export type FormFieldSchemaType = z.infer<z.ZodObject<z.ZodRawShape>>;

export function getFormFieldSchema() {
  const lang = requestContext.requestInfo?.lang.deviceLang ?? 'ko';
  // 현재 언어에 해당하는 schema 가 존재하지 않는 경우에만 초기화합니다.
  if (!FormFieldSchema[lang]) {
    setFormFieldSchema(lang);
  }

  return FormFieldSchema[lang];
}

function setFormFieldSchema(lang: LocaleType) {
  const schema = z.object({
    // categoryId와 subject는 화면에 노출되지 않는 hidden 처리된 필드입니다.
    categoryId: z.number().superRefine(
      buildMetadata({
        uiType: 'text',
        inputProps: {
          type: 'number',
        },
      })
    ),
    subject: z.string().superRefine(
      buildMetadata({
        uiType: 'text',
      })
    ),
    sendEmail: z
      .string()
      .min(1)
      .email(t('more_customer_cs_inquiry_tab_invalid_email'))
      .superRefine(
        buildMetadata({
          uiType: 'text',
          label: t('more_customer_cs_inquiry_tab_email'),
          inputProps: {
            placeholder: t('more_customer_cs_inquiry_tab_email_input'),
            maxLength: 100,
          },
        })
      ),
    telNum: z
      .string()
      .min(1)
      .superRefine(
        buildMetadata({
          uiType: 'text',
          label: t('more_customer_cs_inquiry_tab_telephone'),
          inputProps: {
            placeholder: t('more_customer_cs_inquiry_tab_telephone_input'),
            maxLength: 20,
          },
        })
      ),
    loginId: z.string().optional(),
    projectId: z.string().optional(),
    customerName: z.string().optional(),
    content: getContent(),
    files: z
      .array(IS_CLIENT_SIDE ? z.instanceof(File) : z.any())
      .optional()
      .superRefine(
        buildMetadata({
          uiType: 'fileUpload',
          label: t('more_customer_cs_inquiry_tab_attachment'),
          subLabel: t('more_customer_cs_inquiry_tab_attachment_info'),
          fileUploadProps: {
            placeholder: t('more_customer_cs_inquiry_tab_attachment_button'),
            addButtonLabel: t('more_customer_cs_inquiry_tab_upload_button'),
            accept: ALLOWED_FILE_EXTENSIONS.join(','),
          },
          maxValue: 30,
        })
      ),
    paymentType: z
      .string()
      .min(1)
      .optional()
      .superRefine(
        buildMetadata({
          uiType: 'text',
          label: t('more_customer_cs_inquiry_tab_payment_method'),
          inputProps: {
            maxLength: 20,
            placeholder: t('more_customer_cs_inquiry_tab_payment_method_input'),
          },
        })
      ),
    bankAccount: z
      .string()
      .min(1)
      .optional()
      .superRefine(
        buildMetadata({
          uiType: 'text',
          label: t('more_customer_cs_inquiry_tab_account_number'),
          inputProps: {
            placeholder: t('more_customer_cs_inquiry_tab_account_number_input'),
            maxLength: 20,
          },
        })
      ),
    bankName: z
      .string()
      .min(1)
      .optional()
      .superRefine(
        buildMetadata({
          uiType: 'text',
          label: t('more_customer_cs_inquiry_tab_bank'),
          inputProps: {
            placeholder: t('more_customer_cs_inquiry_tab_bank_input'),
            maxLength: 20,
          },
        })
      ),
    orderNo: getOrderNoField(),
    orderNoRequired: getOrderNoField({isRequired: true}),
    appVersion: z
      .string()
      .optional()
      .superRefine(
        buildMetadata({
          uiType: 'text',
          label: t('more_customer_cs_inquiry_tab_browser'),
          inputProps: {
            placeholder: t('more_customer_cs_inquiry_tab_browser_input'),
            maxLength: 50,
          },
        })
      ),
    osVersion: z
      .string()
      .optional()
      .superRefine(
        buildMetadata({
          uiType: 'text',
          label: t('more_customer_cs_inquiry_tab_os_version'),
          inputProps: {
            placeholder: t('more_customer_cs_inquiry_tab_os_version_input'),
            maxLength: 50,
          },
        })
      ),
    deviceModel: z
      .string()
      .optional()
      .superRefine(
        buildMetadata({
          uiType: 'text',
          label: t('more_customer_cs_inquiry_tab_device'),
          inputProps: {
            placeholder: t('more_customer_cs_inquiry_tab_device_input'),
            maxLength: 50,
          },
        })
      ),
    ggOrderNo: z
      .string()
      .optional()
      .superRefine(
        buildMetadata({
          uiType: 'text',
          label: t('more_customer_cs_inquiry_tab_google_order_number'),
          subLabel: t('more_customer_cs_inquiry_tab_google_order_number_info'),
          inputProps: {
            placeholder: t(
              'more_customer_cs_inquiry_tab_google_order_number_input'
            ),
            maxLength: 50,
          },
        })
      ),
    osOrderNo: z
      .string()
      .optional()
      .superRefine(
        buildMetadata({
          uiType: 'text',
          label: t('more_customer_cs_inquiry_tab_onestore_order_number'),
          subLabel: t(
            'more_customer_cs_inquiry_tab_onestore_order_number_info'
          ),
          inputProps: {
            placeholder: t(
              'more_customer_cs_inquiry_tab_onestore_order_number_input'
            ),
            maxLength: 50,
          },
        })
      ),
    bankAccountNm: z
      .string()
      .optional()
      .superRefine(
        buildMetadata({
          uiType: 'text',
          label: t('more_customer_cs_inquiry_tab_depositor'),
          inputProps: {
            placeholder: t('more_customer_cs_inquiry_tab_depositor_input'),
            maxLength: 20,
          },
        })
      ),
  });

  const defaultInfoFields = schema.pick({
    subject: true,
    categoryId: true,
    sendEmail: true,
    telNum: true,
    loginId: true,
    projectId: true,
    customerName: true,
    osVersion: true,
    deviceModel: true,
    appVersion: true,
  });

  const contentFields = schema.pick({
    content: true,
    files: true,
  });

  const paymentFields = schema.pick({paymentType: true});
  const orderNoField = schema.pick({
    orderNo: true,
  });
  const orderNoRequiredField = schema.pick({
    orderNoRequired: true,
  });
  const storeOrderNoField = schema.pick({
    ggOrderNo: true,
    osOrderNo: true,
  });
  const bankFields = schema.pick({
    bankAccount: true,
    bankName: true,
    bankAccountNm: true,
  });

  const askSchemaMap = {
    [AskType.Default]: {
      [AskSubCategory.Android]: defaultInfoFields.merge(contentFields),
      [AskSubCategory.Ios]: defaultInfoFields.merge(contentFields),
      [AskSubCategory.Web]: defaultInfoFields.merge(contentFields),
      [AskSubCategory.Default]: defaultInfoFields.merge(contentFields),
    },
    [AskType.ServiceUse]: {
      [AskSubCategory.Android]: defaultInfoFields.merge(contentFields).extend({
        content: getContent('more_customer_cs_inquiry_tab_details_general'),
      }),
      [AskSubCategory.Ios]: defaultInfoFields.merge(contentFields).extend({
        content: getContent('more_customer_cs_inquiry_tab_details_general'),
      }),
      [AskSubCategory.Web]: defaultInfoFields.merge(contentFields).extend({
        content: getContent('more_customer_cs_inquiry_tab_details_general'),
      }),
      [AskSubCategory.Default]: defaultInfoFields.merge(contentFields),
    },
    [AskType.Cash]: {
      [AskSubCategory.Android]: defaultInfoFields
        .merge(paymentFields)
        .merge(contentFields)
        .extend({
          content: getContent('more_customer_cs_inquiry_tab_details_cash'),
        }),
      [AskSubCategory.Ios]: defaultInfoFields
        .merge(paymentFields)
        .merge(contentFields)
        .extend({
          content: getContent('more_customer_cs_inquiry_tab_details_cash'),
        }),
      [AskSubCategory.Web]: defaultInfoFields
        .merge(paymentFields)
        .merge(contentFields)
        .extend({
          content: getContent('more_customer_cs_inquiry_tab_details_cash'),
        }),
      [AskSubCategory.Default]: defaultInfoFields.merge(contentFields),
    },
    [AskType.CashChargeError]: {
      [AskSubCategory.Android]: defaultInfoFields
        .merge(paymentFields)
        .merge(storeOrderNoField)
        .merge(contentFields)
        .extend({
          content: getContent(
            'more_customer_cs_inquiry_tab_details_charge_problems'
          ),
        }),
      [AskSubCategory.Ios]: defaultInfoFields
        .merge(paymentFields)
        .merge(storeOrderNoField)
        .merge(contentFields)
        .extend({
          content: getContent(
            'more_customer_cs_inquiry_tab_details_charge_problems'
          ),
        }),
      [AskSubCategory.Web]: defaultInfoFields
        .merge(paymentFields)
        .merge(storeOrderNoField)
        .merge(contentFields)
        .extend({
          content: getContent(
            'more_customer_cs_inquiry_tab_details_charge_problems'
          ),
        }),
      [AskSubCategory.Default]: defaultInfoFields.merge(contentFields),
    },
    [AskType.CashRefund]: {
      [AskSubCategory.Android]: defaultInfoFields
        .merge(bankFields)
        .merge(paymentFields)
        .merge(storeOrderNoField)
        .merge(contentFields)
        .extend({
          content: getContent(
            'more_customer_cs_inquiry_tab_details_refund_cash_android'
          ),
        }),
      [AskSubCategory.Ios]: defaultInfoFields
        .merge(paymentFields)
        .merge(contentFields)
        .extend({
          content: getContent(
            'more_customer_cs_inquiry_tab_details_refund_cash_ios'
          ),
        }),
      [AskSubCategory.Web]: defaultInfoFields
        .merge(bankFields)
        .merge(paymentFields)
        .merge(storeOrderNoField)
        .merge(contentFields)
        .extend({
          content: getContent(
            'more_customer_cs_inquiry_tab_details_refund_cash_web'
          ),
        }),
      [AskSubCategory.Default]: defaultInfoFields.merge(contentFields),
    },
    [AskType.PassUse]: {
      [AskSubCategory.Android]: defaultInfoFields.merge(contentFields).extend({
        content: getContent('more_customer_cs_inquiry_tab_details_ticket'),
      }),
      [AskSubCategory.Ios]: defaultInfoFields.merge(contentFields).extend({
        content: getContent('more_customer_cs_inquiry_tab_details_ticket'),
      }),
      [AskSubCategory.Web]: defaultInfoFields.merge(contentFields).extend({
        content: getContent('more_customer_cs_inquiry_tab_details_ticket'),
      }),
      [AskSubCategory.Default]: defaultInfoFields.merge(contentFields),
    },
    [AskType.CashFriends]: {
      [AskSubCategory.Android]: defaultInfoFields
        .merge(orderNoField)
        .merge(contentFields)
        .extend({
          content: getContent(
            'more_customer_cs_inquiry_tab_details_cashfriends'
          ),
        }),
      [AskSubCategory.Ios]: defaultInfoFields
        .merge(orderNoField)
        .merge(contentFields)
        .extend({
          content: getContent(
            'more_customer_cs_inquiry_tab_details_cashfriends'
          ),
        }),
      [AskSubCategory.Web]: defaultInfoFields
        .merge(orderNoField)
        .merge(contentFields)
        .extend({
          content: getContent(
            'more_customer_cs_inquiry_tab_details_cashfriends'
          ),
        }),
      [AskSubCategory.Default]: defaultInfoFields.merge(contentFields),
    },
    [AskType.CashFriendsShopping]: {
      [AskSubCategory.Android]: defaultInfoFields
        .merge(orderNoRequiredField)
        .merge(contentFields)
        .extend({
          content: getContent(
            'more_customer_cs_inquiry_tab_details_cashfriends_shopping'
          ),
        }),
      [AskSubCategory.Ios]: defaultInfoFields
        .merge(orderNoRequiredField)
        .merge(contentFields)
        .extend({
          content: getContent(
            'more_customer_cs_inquiry_tab_details_cashfriends_shopping'
          ),
        }),
      [AskSubCategory.Web]: defaultInfoFields
        .merge(orderNoRequiredField)
        .merge(contentFields)
        .extend({
          content: getContent(
            'more_customer_cs_inquiry_tab_details_cashfriends_shopping'
          ),
        }),
      [AskSubCategory.Default]: defaultInfoFields.merge(contentFields),
    },
    [AskType.ServiceError]: {
      [AskSubCategory.Android]: defaultInfoFields.merge(contentFields).extend({
        content: getContent('more_customer_cs_inquiry_tab_details_problems'),
      }),
      [AskSubCategory.Ios]: defaultInfoFields.merge(contentFields).extend({
        content: getContent('more_customer_cs_inquiry_tab_details_problems'),
      }),
      [AskSubCategory.Web]: defaultInfoFields.merge(contentFields).extend({
        content: getContent('more_customer_cs_inquiry_tab_details_problems'),
      }),
      [AskSubCategory.Default]: defaultInfoFields.merge(contentFields),
    },
    [AskType.Content]: {
      [AskSubCategory.Android]: defaultInfoFields.merge(contentFields).extend({
        content: getContent('more_customer_cs_inquiry_tab_details_contents'),
      }),
      [AskSubCategory.Ios]: defaultInfoFields.merge(contentFields).extend({
        content: getContent('more_customer_cs_inquiry_tab_details_contents'),
      }),
      [AskSubCategory.Web]: defaultInfoFields.merge(contentFields).extend({
        content: getContent('more_customer_cs_inquiry_tab_details_contents'),
      }),
      [AskSubCategory.Default]: defaultInfoFields.merge(contentFields),
    },
    [AskType.Event]: {
      [AskSubCategory.Android]: defaultInfoFields.merge(contentFields).extend({
        content: getContent('more_customer_cs_inquiry_tab_details_events'),
      }),
      [AskSubCategory.Ios]: defaultInfoFields.merge(contentFields).extend({
        content: getContent('more_customer_cs_inquiry_tab_details_events'),
      }),
      [AskSubCategory.Web]: defaultInfoFields.merge(contentFields).extend({
        content: getContent('more_customer_cs_inquiry_tab_details_events'),
      }),
      [AskSubCategory.Default]: defaultInfoFields.merge(contentFields),
    },
    [AskType.Suggestion]: {
      [AskSubCategory.Android]: defaultInfoFields.merge(contentFields).extend({
        content: getContent('more_customer_cs_inquiry_tab_details_suggestions'),
      }),
      [AskSubCategory.Ios]: defaultInfoFields.merge(contentFields).extend({
        content: getContent('more_customer_cs_inquiry_tab_details_suggestions'),
      }),
      [AskSubCategory.Web]: defaultInfoFields.merge(contentFields).extend({
        content: getContent('more_customer_cs_inquiry_tab_details_suggestions'),
      }),
      [AskSubCategory.Default]: defaultInfoFields.merge(contentFields),
    },
    [AskType.RightsInfringement]: {
      [AskSubCategory.Android]: defaultInfoFields.merge(contentFields).extend({
        content: getContent(
          'more_customer_cs_inquiry_tab_details_report_rights'
        ),
      }),
      [AskSubCategory.Ios]: defaultInfoFields.merge(contentFields).extend({
        content: getContent(
          'more_customer_cs_inquiry_tab_details_report_rights'
        ),
      }),
      [AskSubCategory.Web]: defaultInfoFields.merge(contentFields).extend({
        content: getContent(
          'more_customer_cs_inquiry_tab_details_report_rights'
        ),
      }),
      [AskSubCategory.Default]: defaultInfoFields.merge(contentFields),
    },
  };

  FormFieldSchema[lang] = askSchemaMap;
}

const getContent = (tKey?: TranslateKey) =>
  z
    .string()
    .min(1)
    .default(tKey ? t(tKey) : '')
    .refine(value => tKey && value !== t(tKey), {
      message: t('more_customer_cs_inquiry_tab_details_input'),
    })
    .superRefine(
      buildMetadata({
        uiType: 'textarea',
        label: t('more_customer_cs_inquiry_tab_details'),
        maxValue: 3000,
        textareaProps: {
          maxLength: 3000,
          placeholder: t('more_customer_cs_inquiry_tab_details_input'),
        },
      })
    );

const getOrderNoField = ({isRequired}: {isRequired?: boolean} = {}) =>
  (isRequired ? z.string().min(1) : z.string().optional()).superRefine(
    buildMetadata({
      uiType: 'text',
      label: t('more_customer_cs_inquiry_tab_order_number'),
      subLabel: t('more_customer_cs_inquiry_tab_order_number_info'),
      inputProps: {
        maxLength: 50,
        placeholder: t('more_customer_cs_inquiry_tab_order_number_input'),
      },
    })
  );

export const DEFAULT_HIDDEN_FIELDS = [
  'subject',
  'categoryId',
  'loginId',
  'projectId',
  'customerName',
  'osVersion',
  'deviceModel',
  'appVersion',
];

// 기본적으로 숨김처리지만 예외적으로 default 값이 없는 경우 노출하는 케이스입니다.
export const SHOW_WHEN_DEFAULT_NOT_EXISTS_FIELDS_MAP: Partial<
  Record<AskType, Record<AskSubCategory, FormFieldKeys[]>>
> = {
  [AskType.ServiceError]: {
    [AskSubCategory.Android]: ['osVersion', 'deviceModel'],
    [AskSubCategory.Ios]: ['osVersion', 'deviceModel'],
    [AskSubCategory.Web]: ['osVersion', 'appVersion'],
    [AskSubCategory.Default]: [],
  },
};
